import type { BusinessHours, RuleConditions } from '@mailpilot/shared';
import clsx from 'clsx';
import { ChipInput, Field, Input, Segmented, Switch } from '../ui';
import { SubSection } from './SettingsPrimitives';

type ListKey = Exclude<keyof RuleConditions, 'businessHours'>;

const MATCH_FIELDS: { key: ListKey; label: string; placeholder: string; hint?: string }[] = [
  { key: 'senders', label: 'From these senders', placeholder: 'name@example.com' },
  { key: 'domains', label: 'From these domains', placeholder: 'example.com' },
  { key: 'subjectPhrases', label: 'Subject contains', placeholder: 'invoice, booking…' },
  { key: 'bodyPhrases', label: 'Body contains', placeholder: 'order number…' },
  { key: 'categories', label: 'AI categories', placeholder: 'support, sales…', hint: 'Needs AI analysis to be available.' },
  { key: 'languages', label: 'Languages', placeholder: 'en, fr…', hint: 'ISO codes, e.g. en, de, es.' },
];

const EXCLUDE_FIELDS: { key: ListKey; label: string; placeholder: string }[] = [
  { key: 'excludeSenders', label: 'Never for these senders', placeholder: 'boss@example.com' },
  { key: 'excludeDomains', label: 'Never for these domains', placeholder: 'partner.com' },
  { key: 'excludeSubjectPhrases', label: 'Never when subject contains', placeholder: 'urgent, legal…' },
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ListField({ label, placeholder, hint, value, onChange }: { label: string; placeholder: string; hint?: string; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[0.8125rem] font-medium text-ink">{label}</span>
      <ChipInput label={label} value={value} onChange={onChange} placeholder={`${placeholder} — press Enter`} />
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  );
}

export function ResponderConditions({ value, onChange }: { value: RuleConditions; onChange: (c: RuleConditions) => void }) {
  const setList = (k: ListKey, v: string[]) => onChange({ ...value, [k]: v });
  const bh = value.businessHours;
  const setBh = (patch: Partial<BusinessHours>) => onChange({ ...value, businessHours: { ...bh, ...patch } });
  const toggleDay = (d: number) => setBh({ days: bh.days.includes(d) ? bh.days.filter((x) => x !== d) : [...bh.days, d].sort((a, b) => a - b) });

  return (
    <div className="space-y-6">
      <div className="border-t border-line/70 pt-6">
        <SubSection title="Match when" hint="Every filled-in condition must hold. Leave a field empty to ignore it. Within one field, any entry can match.">
          <div className="grid gap-5 sm:grid-cols-2">
            {MATCH_FIELDS.map((f) => (
              <ListField key={f.key} label={f.label} placeholder={f.placeholder} hint={f.hint} value={value[f.key]} onChange={(v) => setList(f.key, v)} />
            ))}
          </div>
        </SubSection>
      </div>

      <div className="border-t border-line/70 pt-6">
        <SubSection title="Exclusions" hint="These always win over the matches above.">
          <div className="grid gap-5 sm:grid-cols-2">
            {EXCLUDE_FIELDS.map((f) => (
              <ListField key={f.key} label={f.label} placeholder={f.placeholder} value={value[f.key]} onChange={(v) => setList(f.key, v)} />
            ))}
          </div>
        </SubSection>
      </div>

      <div className="border-t border-line/70 pt-6">
        <div className="space-y-5">
          <Switch id="rule-business-hours" checked={bh.enabled} onChange={(enabled) => setBh({ enabled })} label="Business hours" description="Limit this rule to messages received inside or outside your working hours." />
          {bh.enabled && (
            <div className="space-y-5">
              <Segmented
                label="When the rule runs"
                value={bh.outsideOnly ? 'outside' : 'inside'}
                onChange={(v) => setBh({ outsideOnly: v === 'outside' })}
                options={[
                  { value: 'inside', label: 'Inside business hours' },
                  { value: 'outside', label: 'Outside business hours' },
                ]}
              />
              <div>
                <p className="mb-2 text-[0.8125rem] font-medium text-ink">Working days</p>
                <div role="group" aria-label="Working days" className="flex flex-wrap gap-1.5">
                  {DAYS.map((d, i) => {
                    const on = bh.days.includes(i);
                    return (
                      <button
                        key={d}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleDay(i)}
                        className={clsx('h-9 min-w-[3rem] rounded-xl px-2 text-[0.8125rem] font-medium transition-colors', on ? 'bg-primary text-primary-fg shadow-primary' : 'bg-sunken text-muted hover:bg-raised hover:text-ink')}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-3">
                <Field label="Start">{(id) => <Input id={id} type="time" value={bh.start} onChange={(e) => setBh({ start: e.target.value })} />}</Field>
                <Field label="End">{(id) => <Input id={id} type="time" value={bh.end} onChange={(e) => setBh({ end: e.target.value })} />}</Field>
                <Field label="Time zone" hint="IANA name, e.g. Europe/London">
                  {(id) => <Input id={id} value={bh.timezone} onChange={(e) => setBh({ timezone: e.target.value })} />}
                </Field>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
